import React, { ReactNode, useState } from 'react';

import { Box, Loader, Modal, Select, Stack, Text, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useUpdatePrimaryDiagnosisMutation } from '@/store/slices/api/patientDiagnosesApiSlice';
import {
  useGetAuthorizedPractitionersQuery,
  useGetPatientQuery
} from '@/store/slices/api/patientsApiSlice';
import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { MISSING_DIAGNOSIS_REASON } from '@/utils/enrollmentChecklist';
import { getMrnLabel } from '@/utils/getMrnLabel';
import DiagnosisSelect, { LabelAndValue } from '@/components/DiagnosisSelect';

import PatientEditModal from '@/pages/physician/PatientOverviewPage/PatientOverview/patientEditModal';
import DxCodeSelectionModal from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/MedicalInfoDetails/DxCodeSelectionModal';
import { Patient } from '@/types/patient';
import { User } from '@/types/user';

import styles from './cellsV2.module.scss';
import { openPatientChart } from './DeviceCell';

import type { RowChecklist } from '../hooks/useRowChecklist';
import type {
  ChecklistItem,
  ChecklistSection,
  ChecklistSectionKey,
  EnrollmentChecklistResult
} from '@/utils/enrollmentChecklist';

/**
 * Checklist columns (POC ChecklistCells). Colors: Not set gold, Update required orange,
 * Complete and Not required green. An empty diagnosis or demographics cell edits in place.
 * Insurance that still needs work opens that tab on the chart.
 */

export type ChecklistCellState = 'complete' | 'update_required' | 'not_set' | 'not_required';

/** Loading / not-checked / error states shared by every checklist cell; children once ready. */
export const ChecklistGate: React.FC<{ checklist: RowChecklist; children: ReactNode }> = ({
  checklist,
  children
}) => {
  if (!checklist.enabled) return <Text className={styles.muted}>—</Text>;
  if (checklist.loading) return <Loader size="xs" color="plum" />;
  if (checklist.error || !checklist.evaluation) {
    return <Text className={styles.muted}>Couldn&apos;t load</Text>;
  }
  return <>{children}</>;
};

function findSection(
  evaluation: EnrollmentChecklistResult,
  key: ChecklistSectionKey
): ChecklistSection | undefined {
  return evaluation.sections.find((section) => section.key === key);
}

function findItem(
  evaluation: EnrollmentChecklistResult,
  sectionKey: ChecklistSectionKey,
  itemKey: string
): ChecklistItem | undefined {
  return findSection(evaluation, sectionKey)?.items.find((item) => item.key === itemKey);
}

/** A missing item that has a value on file needs fixing; one with nothing on file is not set. */
function itemState(item: ChecklistItem | undefined): ChecklistCellState {
  if (!item || !item.required) return 'not_required';
  if (item.complete) return 'complete';
  return item.value ? 'update_required' : 'not_set';
}

function sectionState(section: ChecklistSection | undefined): ChecklistCellState {
  if (!section || section.status === 'not_required') return 'not_required';
  if (section.status === 'complete') return 'complete';
  const missing = section.items.filter((item) => item.required && !item.complete);
  return missing.some((item) => Boolean(item.value)) ? 'update_required' : 'not_set';
}

/** POC palette (poc colors.ts): yellow #d9a206, green #2faa6a, orange.8 #e8590c. */
const POC_YELLOW = '#d9a206';
const POC_GREEN = '#2faa6a';
const POC_ORANGE = '#e8590c';

const StatusLabel = ({ children, color }: { children: string; color: string }) => (
  <Text size="sm" c={color} fw={500}>
    {children}
  </Text>
);

const NotSet = () => <StatusLabel color={POC_YELLOW}>Not set</StatusLabel>;

const NotRequired = () => <StatusLabel color={POC_GREEN}>Not required</StatusLabel>;

const CompleteLabel = () => <StatusLabel color={POC_GREEN}>Complete</StatusLabel>;

const StatusButton = ({
  color,
  children,
  onClick
}: {
  color: string;
  children: string;
  onClick: () => void;
}) => (
  <UnstyledButton
    onClick={onClick}
    style={{
      color,
      fontWeight: 500,
      fontSize: 14,
      lineHeight: 1.2,
      cursor: 'pointer',
      textDecoration: 'underline dotted',
      textUnderlineOffset: 3
    }}>
    {children}
  </UnstyledButton>
);

function overviewFromPatient(patient: Patient) {
  return {
    id: patient.id,
    firstName: patient.givenName || undefined,
    lastName: patient.familyName || undefined,
    preferredName: patient.preferredName || undefined,
    address1: patient.address?.line1 || undefined,
    address2: patient.address?.line2 || undefined,
    city: patient.address?.city || undefined,
    state: patient.address?.state || undefined,
    zip: patient.address?.postalCode || undefined,
    phoneNumber: patient.phoneNumber || undefined,
    homePhone: patient.homePhone || undefined,
    otherPhone: patient.otherPhone || undefined,
    sex: patient.sex || undefined,
    mrn2: patient.mrn2 || undefined,
    birthDate: patient.birthDate || undefined,
    addressId: patient.address?.id || undefined
  };
}

const UpdateRequired = () => <StatusLabel color={POC_ORANGE}>Update required</StatusLabel>;

/** Code on the first line. A device-default code keeps that note underneath. */
function diagnosisLines(value?: string): { code: string; detail?: string } | null {
  if (!value) return null;
  const deviceDefault = value.match(/^(.*) \(device default\)$/);
  if (deviceDefault) return { code: deviceDefault[1], detail: 'Device default' };
  return { code: value };
}

const StateText: React.FC<{ state: ChecklistCellState; value?: string }> = ({ state, value }) => {
  switch (state) {
    case 'complete':
      return value ? (
        <Text className={styles.cell} lineClamp={2}>
          {value}
        </Text>
      ) : (
        <CompleteLabel />
      );
    case 'update_required':
      return value ? (
        <Stack gap={0}>
          <Text className={styles.cell} lineClamp={2}>
            {value}
          </Text>
          <UpdateRequired />
        </Stack>
      ) : (
        <UpdateRequired />
      );
    case 'not_set':
      return <NotSet />;
    case 'not_required':
      return <NotRequired />;
  }
};

const CellFrame: React.FC<{
  checklist: RowChecklist;
  fill?: boolean;
  render: (evaluation: EnrollmentChecklistResult) => ReactNode;
}> = ({ checklist, fill, render }) => (
  <Box
    style={{
      display: fill ? 'block' : 'inline-flex',
      alignItems: 'center',
      minHeight: 20,
      width: fill ? '100%' : undefined
    }}>
    <ChecklistGate checklist={checklist}>
      {checklist.evaluation ? render(checklist.evaluation) : null}
    </ChecklistGate>
  </Box>
);

interface ChecklistCellProps {
  checklist: RowChecklist;
  patientId?: string;
}

/** Set a missing diagnosis the same way Medical Info does. */
const DiagnosisSetter = ({
  patientId,
  multiple,
  onClose
}: {
  patientId: string;
  multiple: boolean;
  onClose: () => void;
}) => {
  const [updatePatient] = useUpdatePatientMutation();
  const [updatePrimaryDiagnosis] = useUpdatePrimaryDiagnosisMutation();

  const saveOption = async (diagnosis: LabelAndValue | null) => {
    if (!diagnosis) return;
    try {
      await updatePatient({
        patient: { id: patientId, diagnosisOption: diagnosis.value }
      }).unwrap();
      notifications.show({
        title: 'Success',
        message: 'Diagnosis Code updated successfully.',
        color: 'green'
      });
      onClose();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update Diagnosis Code.',
        color: 'red'
      });
    }
  };

  if (multiple) {
    return (
      <DxCodeSelectionModal
        opened
        patientId={patientId}
        onClose={onClose}
        onChange={() => undefined}
        onSave={async (diagnosis, diagnosisId) => {
          if (diagnosisId) {
            await updatePrimaryDiagnosis({ diagnosisId, patientId }).unwrap();
            return;
          }
          await saveOption(diagnosis);
        }}
      />
    );
  }

  return (
    <Modal opened onClose={onClose} title="Primary Diagnosis Code" size="md">
      <DiagnosisSelect
        name="diagnosisOption"
        category="patient"
        onChange={(value) => {
          void saveOption(value ?? null);
        }}
      />
    </Modal>
  );
};

/** Diagnosis code(s) on file (or the device default), from the Clinical section. */
export const DiagnosisCell: React.FC<ChecklistCellProps> = ({ checklist, patientId }) => {
  const [open, setOpen] = useState(false);
  return (
    <CellFrame
      checklist={checklist}
      render={(evaluation) => {
        const item = findItem(evaluation, 'clinical', 'diagnosis');
        const state = itemState(item);
        const lines = diagnosisLines(item?.value);
        const missingCode = !lines && state !== 'not_required' && state !== 'complete';
        const multiple = item?.reason === MISSING_DIAGNOSIS_REASON;
        return (
          <>
            {missingCode ? (
              <StatusButton
                color={multiple ? POC_ORANGE : POC_YELLOW}
                onClick={() => patientId && setOpen(true)}>
                {multiple ? 'Missing diagnosis code' : 'Not set'}
              </StatusButton>
            ) : lines ? (
              <Stack gap={0}>
                <Text className={styles.cell} lineClamp={2}>
                  {lines.code}
                </Text>
                {lines.detail ? (
                  <Text className={styles.sub} lineClamp={2}>
                    {lines.detail}
                  </Text>
                ) : null}
                {state === 'update_required' ? <UpdateRequired /> : null}
              </Stack>
            ) : (
              <StateText state={state} />
            )}
            {open && patientId && (
              <DiagnosisSetter
                patientId={patientId}
                multiple={multiple}
                onClose={() => setOpen(false)}
              />
            )}
          </>
        );
      }}
    />
  );
};

/** Following practitioner. Inline select; the option list loads when the menu opens. */
export const FollowingCell: React.FC<ChecklistCellProps> = ({ checklist, patientId }) => {
  const { data: patient } = useGetPatientQuery(patientId ?? '', {
    skip: !patientId || !checklist.enabled
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: practitioners, isFetching } = useGetAuthorizedPractitionersQuery(
    { patientId: patientId ?? '' },
    { skip: !menuOpen || !patientId }
  );
  const [updatePatient, { isLoading: saving }] = useUpdatePatientMutation();

  return (
    <CellFrame
      checklist={checklist}
      fill
      render={(evaluation) => {
        const item = findItem(evaluation, 'clinic', 'practitioner');
        const required = Boolean(item?.required);
        const currentId = patient?.practitioner?.id ?? null;
        const savedNpi = patient?.practitionerNpi?.trim() || '';
        const currentName =
          item?.value ||
          [patient?.practitioner?.given, patient?.practitioner?.family].filter(Boolean).join(' ');
        const loaded =
          practitioners?.map((row) => ({
            value: row.id,
            label: [row.given, row.family].filter(Boolean).join(' ')
          })) ?? [];
        const options =
          currentId && currentName && !loaded.some((option) => option.value === currentId)
            ? [{ value: currentId, label: currentName }, ...loaded]
            : loaded;
        const placeholder = currentId
          ? undefined
          : savedNpi
            ? `NPI ${savedNpi}`
            : required
              ? 'Not set'
              : 'Not required';

        return (
          <Select
            aria-label="Following practitioner"
            size="xs"
            w="100%"
            data={options}
            value={currentId}
            placeholder={placeholder}
            searchable
            allowDeselect={false}
            disabled={!patientId || saving}
            nothingFoundMessage={isFetching ? 'Loading…' : 'No authorized practitioners'}
            comboboxProps={{ withinPortal: true, zIndex: 400 }}
            onDropdownOpen={() => setMenuOpen(true)}
            rightSection={saving || (menuOpen && isFetching) ? <Loader size={12} /> : undefined}
            styles={{
              input: {
                minHeight: 28,
                fontSize: 13,
                fontWeight: 500,
                color: currentId || savedNpi ? '#2c0817' : required ? POC_YELLOW : POC_GREEN
              }
            }}
            onChange={(value) => {
              if (!value || !patientId || value === currentId) return;
              void updatePatient({
                patient: {
                  id: patientId,
                  practitioner: { id: value } as User
                }
              })
                .unwrap()
                .then(() => {
                  notifications.show({
                    title: 'Success',
                    message: 'Following practitioner updated',
                    color: 'green'
                  });
                })
                .catch(() => {
                  notifications.show({
                    title: 'Error',
                    message: 'Could not update the following practitioner. Please try again.',
                    color: 'red'
                  });
                });
            }}
          />
        );
      }}
    />
  );
};

/**
 * List demographics column. The clinic toggle, date of birth, and the MRN shown on the
 * patient cell (mrn1). Address, phone, and sex stay on the chart checklist.
 */
export function listDemographicsState(
  demographicsRequired: boolean,
  dob?: string | null,
  mrn?: string | null
): ChecklistCellState {
  if (!demographicsRequired) return 'not_required';
  if (!dob?.trim() || !mrn?.trim()) return 'not_set';
  return 'complete';
}

export const DemographicsCell: React.FC<
  ChecklistCellProps & { dob?: string | null; mrn?: string | null }
> = ({ checklist, patientId, dob, mrn }) => {
  const [open, setOpen] = useState(false);
  const { data: full } = useGetPatientQuery(patientId ?? '', {
    skip: !patientId || !checklist.enabled
  });
  const overview = full ? overviewFromPatient(full) : null;
  return (
    <CellFrame
      checklist={checklist}
      render={(evaluation) => {
        const birthDate = findItem(evaluation, 'demographics', 'birthDate');
        const state = listDemographicsState(
          Boolean(birthDate?.required),
          full?.birthDate || dob,
          full?.mrn1 || mrn
        );
        return (
          <>
            {state === 'not_set' ? (
              <StatusButton color={POC_YELLOW} onClick={() => setOpen(true)}>
                Not set
              </StatusButton>
            ) : (
              <StateText state={state} />
            )}
            {open && overview && (
              <PatientEditModal
                isOpen
                onClose={() => setOpen(false)}
                patient={overview}
                mrnLabel={getMrnLabel(full?.clinic?.useMpiLabel)}
              />
            )}
          </>
        );
      }}
    />
  );
};

/** Primary insurance (required on split bill accounts). */
export const InsuranceCell: React.FC<ChecklistCellProps> = ({ checklist, patientId }) => (
  <CellFrame
    checklist={checklist}
    render={(evaluation) => {
      const state = sectionState(findSection(evaluation, 'insurance'));
      if ((state === 'not_set' || state === 'update_required') && patientId) {
        return (
          <StatusButton
            color={state === 'not_set' ? POC_YELLOW : POC_ORANGE}
            onClick={() => openPatientChart(patientId, 'insurance')}>
            {state === 'not_set' ? 'Not set' : 'Update required'}
          </StatusButton>
        );
      }
      return <StateText state={state} />;
    }}
  />
);
